import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

export type HeroVideoHandle = { playWithSound: () => void };

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const HeroVideo = forwardRef<HeroVideoHandle>(function HeroVideo(_, ref) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reduced] = useState(prefersReducedMotion);
  const shouldPlay = useRef(!reduced);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !("IntersectionObserver" in window)) { return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (shouldPlay.current) { v.play().catch(() => {}); }
      } else if (!v.paused) {
        v.pause();
      }
    }, { threshold: 0.25 });
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const start = (withSound: boolean) => {
    const v = videoRef.current;
    if (!v) { return; }
    if (withSound) { v.muted = false; setMuted(false); setUnlocked(true); }
    v.currentTime = 0;
    shouldPlay.current = true;
    v.play().catch(() => {});
  };

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) { return; }
    if (!unlocked) { start(true); return; }
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  useImperativeHandle(ref, () => ({
    playWithSound: () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r && (r.top < 0 || r.bottom > window.innerHeight)) {
        wrapRef.current?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
      }
      start(true);
    },
  }));

  const label = !unlocked ? "Tap for sound" : muted ? "Sound on" : "Mute";

  return (
    <div ref={wrapRef} className="relative w-full aspect-video rounded-[12px] overflow-hidden border bg-muted shadow-lg">
      <video
        ref={videoRef}
        data-testid="hero-video"
        className="absolute inset-0 h-full w-full object-cover"
        src="/video/iqw-promo.mp4"
        poster="/video/iqw-promo-poster.jpg"
        autoPlay={!reduced}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label="Instant Quote Wizard product walkthrough"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {reduced && !playing && (
        <button
          type="button"
          onClick={() => start(false)}
          data-testid="hero-video-play"
          aria-label="Play video"
          className="absolute inset-0 m-auto h-16 w-16 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Play className="h-7 w-7 ml-1" />
        </button>
      )}
      <button
        type="button"
        onClick={toggleSound}
        data-testid="hero-video-sound"
        aria-pressed={unlocked && !muted}
        className="absolute bottom-3 left-3 min-h-[44px] px-4 inline-flex items-center gap-2 rounded-full bg-black/70 text-white text-sm font-medium backdrop-blur-sm hover:bg-black/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
      >
        {unlocked && !muted ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        {label}
      </button>
    </div>
  );
});
